/**
 * @file api.js
 * @description This file contains mock API service implementations for user authentication,
 * including email/password registration/login and Casdoor OAuth callback handling.
 * It uses an in-memory store `mockData` to simulate a database.
 * Some parts are now being replaced by actual API calls to the FastAPI backend.
 */
import { API_BASE_URL } from '../config';
import { useAuthStore } from '../stores/authStore';

// Helper to get auth token
const getAuthToken = () => localStorage.getItem('authToken'); // Assuming 'authToken' is the key used by authStore

// Helper for making authenticated API calls
export async function fetchWithAuth(url, options = {}) {
    const token = getAuthToken();
    const headers = { ...options.headers }; // Start with headers from options

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // Do not set Content-Type if body is FormData; browser sets it with boundary
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }
    // else: For FormData, Content-Type is set by the browser.
    // Removing it from default headers if it was there from options for some reason:
    // else if (headers['Content-Type'] && headers['Content-Type'].includes('multipart/form-data')) {
    //    delete headers['Content-Type']; // Let browser handle it
    // }


    const response = await fetch(url, { 
        ...options, 
        headers,
        credentials: 'include' // 确保所有API调用都包含credentials
    });

    if (!response.ok) {
        if (response.status === 401) {
            const authStore = useAuthStore();
            authStore.logout();
            // Redirect to login page
            window.location.href = '/login';
        }
        // Try to parse error from backend if JSON, otherwise use statusText
        let errorDetail = `API Error: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            errorDetail = errorData.detail || errorDetail;
        } catch (e) { /* Ignore if error response is not JSON */ }

        const error = new Error(errorDetail);
        error.status = response.status;
        error.response = response; // Attach full response if needed
        console.error(`API call failed: ${url}`, errorDetail); // Log error
        throw error;
    }
    // For 204 No Content, response.json() will fail.
    if (response.status === 204) {
        return null;
    }
    return response.json();
}

// Helper for calling public endpoints that do not require authentication
async function fetchPublic(url, options = {}) {
    const headers = { ...options.headers };

    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    const response = await fetch(url, {
        ...options,
        headers
    });

    if (!response.ok) {
        let errorDetail = `API Error: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            errorDetail = errorData.detail || errorDetail;
        } catch (e) {
            /* ignore non-JSON error responses */
        }

        const error = new Error(errorDetail);
        error.status = response.status;
        throw error;
    }

    if (response.status === 204) {
        return null;
    }

    return response.json();
}


/**
 * In-memory data store for simulating a database.
 * collections and assets are now managed by the backend.
 */
const mockData = {
  users: [],
  verificationCodes: {},
  loginAttempts: {},
  feedbacks: []
  // notifications array was here, now removed.
};

const MOCK_API_DELAY = 500;
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 5 * 60 * 1000;

// --- Existing Auth functions (sendVerificationCode, registerUser, loginUser) remain unchanged ---
export const sendVerificationCode = (email) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      if (mockData.users.find(user => user.email === email)) {
        resolve({ success: false, message: '该邮箱已被注册。' });
      } else {
        const code = Math.floor(100000 + Math.random() * 900000).toString();
        const expiresAt = Date.now() + 5 * 60 * 1000;
        mockData.verificationCodes[email] = { code, expiresAt };
        console.log(`Mock verification code for ${email}: ${code}`);
        resolve({ success: true, message: '验证码已发送至您的邮箱，5分钟内有效。' });
      }
    }, MOCK_API_DELAY);
  });
};

export const registerUser = (userData) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const { email, password, verificationCode } = userData;
      if (mockData.users.find(user => user.email === email)) {
        resolve({ success: false, message: '该邮箱已被注册。' }); return;
      }
      const storedCodeData = mockData.verificationCodes[email];
      if (!storedCodeData) { resolve({ success: false, message: '请先发送验证码。' }); return; }
      if (Date.now() > storedCodeData.expiresAt) { resolve({ success: false, message: '验证码已过期，请重新发送。' }); return; }
      if (storedCodeData.code !== verificationCode) { resolve({ success: false, message: '验证码错误。' }); return; }
      const hashedPassword = `hashed_${password}`;
      mockData.users.push({ email, hashedPassword, id: `user_${Date.now()}` });
      delete mockData.verificationCodes[email];
      console.log('Mock registered users:', mockData.users);
      resolve({ success: true, message: '注册成功！' });
    }, MOCK_API_DELAY);
  });
};

export const loginUser = (credentials) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const { email, password } = credentials;
      const now = Date.now();
      if (mockData.loginAttempts[email] && mockData.loginAttempts[email].count >= MAX_LOGIN_ATTEMPTS) {
        if (now < mockData.loginAttempts[email].unlockAt) {
          resolve({ success: false, message: '账户因登录尝试次数过多而被锁定。请稍后再试。' }); return;
        } else { delete mockData.loginAttempts[email]; }
      }
      const user = mockData.users.find(u => u.email === email);
      if (!user || user.hashedPassword !== `hashed_${password}`) {
        if (!mockData.loginAttempts[email]) mockData.loginAttempts[email] = { count: 0, unlockAt: 0 };
        mockData.loginAttempts[email].count++;
        if (mockData.loginAttempts[email].count >= MAX_LOGIN_ATTEMPTS) mockData.loginAttempts[email].unlockAt = now + LOCKOUT_DURATION;
        resolve({ success: false, message: '邮箱或密码错误。' });
      } else {
        delete mockData.loginAttempts[email];
        const mockToken = `mock_token_${email}_${now}`;
        resolve({ success: true, token: mockToken, message: '登录成功！', user: {id: user.id, email: user.email, name: user.name || email.split('@')[0]} });
      }
    }, MOCK_API_DELAY);
  });
};

export const handleCasdoorCallback = async (authCode, authState) => {
  const response = await fetch(`${API_BASE_URL}/auth/casdoor-callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // 确保包含credentials以支持CORS
    body: JSON.stringify({ code: authCode, state: authState }),
  });

  if (!response.ok) {
    // Try to parse error from backend if JSON, otherwise use statusText
    let errorDetail = `API Error: ${response.status} ${response.statusText}`;
    try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
    } catch (e) { /* Ignore if error response is not JSON */ }
    const error = new Error(errorDetail);
    error.status = response.status;
    throw error;
  }
  return response.json();
};

// --- Collections API ---
export const getCollections = async () => {
  return fetchWithAuth(`${API_BASE_URL}/collections/`);
};

export const deleteCollection = async (collectionId) => {
  return fetchWithAuth(`${API_BASE_URL}/collections/${collectionId}`, { method: 'DELETE' });
};

export const createCollection = async (collectionData) => {
  const payload = {
      name: collectionData.name,
      asset_codes: collectionData.assets || []
  };
  return fetchWithAuth(`${API_BASE_URL}/collections/`, {
      method: 'POST',
      body: JSON.stringify(payload),
  });
};

export const updateCollection = async (collectionId, collectionData) => {
  if (collectionData.name === undefined) {
       console.warn("updateCollection called without a name. Only name updates are supported.");
       return Promise.reject(new Error("updateCollection now only supports name changes. Use add/remove asset functions for asset changes. If only assets changed, this call is not needed."));
  }
  const payload = { name: collectionData.name };
  return fetchWithAuth(`${API_BASE_URL}/collections/${collectionId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
  });
};

export const getCollectionDetails = async (collectionId) => {
  return fetchWithAuth(`${API_BASE_URL}/collections/${collectionId}`);
};

export const removeAssetFromCollection = async (collectionId, assetCode) => {
  return fetchWithAuth(`${API_BASE_URL}/collections/${collectionId}/assets/${assetCode}`, { method: 'DELETE' });
};

export const addAssetToCollection = async (collectionId, assetCode) => {
  const payload = { asset_code: assetCode };
  return fetchWithAuth(`${API_BASE_URL}/collections/${collectionId}/assets`, {
      method: 'POST',
      body: JSON.stringify(payload),
  });
};

// --- Assets API ---
export const searchAssets = async (query) => {
  const response = await fetch(`${API_BASE_URL}/assets/search/?query=${encodeURIComponent(query)}`);
  if (!response.ok) {
      const error = new Error(`Search API Error: ${response.status} ${response.statusText}`);
      error.status = response.status;
      throw error;
  }
  return response.json();
};

// --- Strategies API ---
export const getStrategies = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/strategies/`);
    if (!response.ok) {
      let errorDetail = `API Error: ${response.status} ${response.statusText}`;
      try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorDetail;
      } catch (e) { /* Ignore if error response is not JSON */ }

      const error = new Error(errorDetail);
      error.status = response.status;
      console.error(`Failed to fetch strategies: ${errorDetail}`);
      throw error;
    }
    return response.json();
  } catch (error) {
    console.error('Error in getStrategies:', error);
    throw error;
  }
};

// --- Notifications API ---
export const getNotifications = async (page = 1, limit = 10, filterType = 'all') => {
  console.log(`api.js: getNotifications called with page: ${page}, limit: ${limit}, filterType: ${filterType}`);
  const params = new URLSearchParams();
  params.append('skip', (page - 1) * limit);
  params.append('limit', limit);

  if (filterType === 'unread') {
    params.append('notification_status', 'unread');
  } else if (filterType === 'simulation' || filterType === 'system') {
    params.append('filter_type', filterType);
  } else if (filterType !== 'all') {
    params.append('filter_type', filterType);
  }

  const url = `${API_BASE_URL}/notifications/?${params.toString()}`;
  return fetchWithAuth(url);
};

export const markNotificationAsRead = async (notificationId) => {
  const url = `${API_BASE_URL}/notifications/${notificationId}/mark-as-read`;
  return fetchWithAuth(url, { method: 'POST' });
};

export const markAllNotificationsAsRead = async () => {
    const url = `${API_BASE_URL}/notifications/mark-all-as-read`;
    return fetchWithAuth(url, { method: 'POST' });
};

export const deleteNotification = async (notificationId) => {
  const url = `${API_BASE_URL}/notifications/${notificationId}`;
  await fetchWithAuth(url, { method: 'DELETE' });
  return { success: true };
};

export const getUnreadNotificationCount = async () => {
  const url = `${API_BASE_URL}/notifications/unread_count`;
  const result = await fetchWithAuth(url);
  return result.unread_count;
};

// --- Simulations API (Mocked parts to be replaced) ---
export const getSimulationHistory = async (filters = {}) => {
    // console.log('[API MOCK] getSimulationHistory called'); // To be replaced
    const params = new URLSearchParams();
    if (filters.page && filters.limit) {
        params.append('skip', (filters.page - 1) * filters.limit);
        params.append('limit', filters.limit);
    } else {
        params.append('skip', '0');
        params.append('limit', '100');
    }
    if (filters.status) {
        params.append('simulation_status', filters.status);
    }

    const url = `${API_BASE_URL}/simulations/?${params.toString()}`;
    return fetchWithAuth(url);
};

export const submitFeedback = async (feedbackData) => {
  // feedbackData is expected to be an object like:
  // { category: '...', description: '...', attachmentFile: File_Object_Or_Null }

  const formData = new FormData();
  formData.append('category', feedbackData.category);
  formData.append('description', feedbackData.description);

  if (feedbackData.attachmentFile && feedbackData.attachmentFile instanceof File) {
    formData.append('attachment_file', feedbackData.attachmentFile, feedbackData.attachmentFile.name);
  }

  return fetchWithAuth(`${API_BASE_URL}/feedback/`, {
    method: 'POST',
    body: formData, // Pass FormData directly
  });
};

export const getSimulationConfigForEdit = async (simulationId) => { // Alias for getSimulationDetails
    // console.log(`[API MOCK] getSimulationConfigForEdit called for ID: ${simulationId}`); // To be replaced
    const url = `${API_BASE_URL}/simulations/${simulationId}`;
    return fetchWithAuth(url);
};
export const getSimulationDetails = getSimulationConfigForEdit; // Keep both if frontend uses both

export const runDraftSimulation = async (originalDraftObject, newName) => {
    // originalDraftObject is the full simulation object from the history page.
    // Its parameters are in backend format (e.g., trading_cost_bps).
    // saveSimulationConfig expects parameters in frontend format (e.g., tradingCost as decimal).

    if (!originalDraftObject || !originalDraftObject.parameters) {
        console.error("runDraftSimulation: originalDraftObject or its parameters are undefined.", originalDraftObject);
        throw new Error("Invalid draft data provided to runDraftSimulation.");
    }

    // Prepare the config object for saveSimulationConfig
    const configForSave = {
        name: newName || originalDraftObject.name, // Use new name or original draft name
        parameters: {
            tradingCost: (originalDraftObject.parameters.trading_cost_bps ?? 0) / 10000,
            startDate: originalDraftObject.parameters.start_date,
            endDate: originalDraftObject.parameters.end_date,
            stopLoss: originalDraftObject.parameters.stop_loss_percentage,
            direction: originalDraftObject.parameters.direction,
            // Ensure all parameters expected by saveSimulationConfig's payload are here
        },
        collectionId: originalDraftObject.collection_id, // Use collection_id from draft
        strategyId: originalDraftObject.strategy_id,     // Use strategy_id from draft
        // Do not pass 'id', 'status', 'createdAt', 'updatedAt', 'userId', 'result', 'cache_key'
        // as saveSimulationConfig is for creating a new simulation.
    };

    const savedSimulationResponse = await saveSimulationConfig(configForSave);

    // saveSimulationConfig now returns { success: true, simulation: object }
    if (savedSimulationResponse && savedSimulationResponse.success && savedSimulationResponse.simulation?.id) {
        // The simulation is created and queued/drafted by saveSimulationConfig.
        // The "run" part for a draft usually means creating a new queued simulation from its settings.
        // The backend's POST /simulations/{id}/run is for an existing simulation.
        // So, returning the newly created simulation is the correct action here.
        return { success: true, newSimulation: savedSimulationResponse.simulation };
    } else {
        console.error("Failed to save simulation from draft or missing ID in response.", savedSimulationResponse);
        throw new Error(savedSimulationResponse?.message || "Failed to save simulation configuration from draft.");
    }
};

export const saveSimulationConfig = async (configData) => {
    // console.log('[API MOCK] saveSimulationConfig called with:', configData); // To be replaced
    const params = { ...configData.parameters };
    if (params.startDate && params.startDate instanceof Date) {
        params.startDate = params.startDate.toISOString().split('T')[0];
    }
    if (params.endDate && params.endDate instanceof Date) {
        params.endDate = params.endDate.toISOString().split('T')[0];
    }

    const payload = {
        name: configData.name,
        parameters: {
            trading_cost_bps: Math.round((configData.parameters.tradingCost || 0) * 10000),
            start_date: params.startDate,
            end_date: params.endDate,
            stop_loss_percentage: configData.parameters.stopLoss,
            direction: configData.parameters.direction,
        },
        collection_id: configData.collectionId,
        strategy_id: configData.strategyId,
    };

    const url = `${API_BASE_URL}/simulations/`;
    // If fetchWithAuth throws an error, it will propagate up, which is handled
    // by the try...catch in CreateSimulationPage.vue.
    const simulation = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    // If fetchWithAuth is successful (doesn't throw), then we have the simulation object.
    return { success: true, simulation: simulation };
};

/**
 * Triggers a specific simulation to run by its ID.
 * @param {string} simulationId - The ID of the simulation to run.
 * @returns {Promise<object>} A promise that resolves with the simulation result from the backend.
 */
export const runSimulationById = async (simulationId) => {
  const url = `${API_BASE_URL}/simulations/${simulationId}/run`;
  return fetchWithAuth(url, { method: 'POST' });
};

export const deleteSimulation = async (simulationId) => {
    // console.log(`[API MOCK] deleteSimulation called for ID: ${simulationId}`); // To be replaced
    const url = `${API_BASE_URL}/simulations/${simulationId}`;
    await fetchWithAuth(url, { method: 'DELETE' });
    return { success: true }; // For compatibility
};


// --- KLine Data API ---
export const getAssetKLineData = async (assetId, startDateString, endDateString) => {
  // Ensure startDateString and endDateString are in 'YYYY-MM-DD' format
  // The browser's toISOString().split('T')[0] or a date library like date-fns/moment can ensure this.
  // For this example, we assume they are already correctly formatted.

  const url = `${API_BASE_URL}/marketdata/kline/${assetId}?start_date=${encodeURIComponent(startDateString)}&end_date=${encodeURIComponent(endDateString)}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Add Authorization header if this endpoint becomes protected
        // 'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      let errorDetail = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {
        // Ignore if error response is not JSON
      }
      const error = new Error(errorDetail);
      error.status = response.status;
      console.error(`Failed to fetch K-line data for asset ID ${assetId}: ${errorDetail}`);
      throw error;
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error fetching K-line data for asset ID ${assetId}:`, error);
    throw error;
  }
};

/**
 * Cancels a specific simulation by its ID.
 * @param {string} simulationId - The ID of the simulation to cancel.
 * @returns {Promise<object>} A promise that resolves with the updated simulation config from the backend.
 */
export const cancelSimulation = async (simulationId) => {
  const url = `${API_BASE_URL}/simulations/${simulationId}/cancel`;
  return fetchWithAuth(url, { method: 'POST' });
};




// --- Trading API ---

/**
 * Get user's trading positions
 * @param {Object} filters - Optional filters (strategy_id, is_active)
 * @returns {Promise<Object>} A promise that resolves with positions list
 */
export const getTradingPositions = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.strategy_id) {
    params.append('strategy_id', filters.strategy_id);
  }
  if (filters.is_active !== undefined) {
    params.append('is_active', filters.is_active);
  }
  
  const url = `${API_BASE_URL}/trading/positions/?${params.toString()}`;
  return fetchWithAuth(url);
};

/**
 * Create a new trading position
 * @param {Object} positionData - Position data (asset_id, strategy_id, entry_price)
 * @returns {Promise<Object>} A promise that resolves with the created position
 */
export const createTradingPosition = async (positionData) => {
  const url = `${API_BASE_URL}/trading/positions/`;
  return fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify(positionData),
  });
};

/**
 * Update a trading position
 * @param {string} positionId - Position ID
 * @param {Object} updateData - Update data (entry_price, is_active)
 * @returns {Promise<Object>} A promise that resolves with the updated position
 */
export const updateTradingPosition = async (positionId, updateData) => {
  const url = `${API_BASE_URL}/trading/positions/${positionId}`;
  return fetchWithAuth(url, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
};

/**
 * Delete a trading position
 * @param {string} positionId - Position ID
 * @returns {Promise<Object>} A promise that resolves when position is deleted
 */
export const deleteTradingPosition = async (positionId) => {
  const url = `${API_BASE_URL}/trading/positions/${positionId}`;
  return fetchWithAuth(url, {
    method: 'DELETE',
  });
};

/**
 * Batch search for assets by name or code, filtered by strategy region
 * @param {Array<string>} assetQueries - Array of asset names or codes
 * @param {number} strategyId - Strategy ID to determine region filter
 * @returns {Promise<Object>} A promise that resolves with found assets and not found queries
 */
export const batchSearchAssets = async (assetQueries, strategyId) => {
  const url = `${API_BASE_URL}/trading/assets/batch-search/`;
  return fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify({
      asset_queries: assetQueries,
      strategy_id: strategyId
    }),
  });
};

/**
 * Create multiple trading positions in batch
 * @param {Array<Object>} positions - Array of position data objects
 * @returns {Promise<Object>} A promise that resolves with created and failed positions
 */
export const createBatchTradingPositions = async (positions) => {
  const url = `${API_BASE_URL}/trading/positions/batch/`;
  return fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify({
      positions: positions
    }),
  });
};

/**
 * Retrieves the aggregated price range analysis summary for the entry monitoring page.
 * Replaces the old getEntrySignals and getFilteredEntrySignals functions.
 * @param {Object} [filters={}] - Optional filters.
 * @param {string} [filters.analysis_date] - The date for the analysis in 'YYYY-MM-DD' format. If not provided, the backend defaults to the latest.
 * @param {string} [filters.strategy_name] - The strategy name to filter by. If not provided, returns all strategies.
 * @param {string} [filters.strategy_id] - The strategy ID to filter by. If not provided, returns all strategies.
 * @returns {Promise<Object>} A promise that resolves with the grouped analysis summary.
 */
export const getPriceRangeAnalysisSummary = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.analysis_date) {
    params.append('analysis_date', filters.analysis_date);
  }
  if (filters.strategy_name) {
    params.append('strategy_name', filters.strategy_name);
  }
  if (filters.strategy_id) {
    params.append('strategy_id', filters.strategy_id);
  }

  const url = `${API_BASE_URL}/price-range-analysis/summary?${params.toString()}`;
  
  // Direct API call
  return fetchWithAuth(url);
};

/**
 * Get available strategies for price range analysis
 * @param {Object} [filters={}] - Optional filters.
 * @param {string} [filters.analysis_date] - The date for the analysis in 'YYYY-MM-DD' format. If not provided, the backend defaults to the latest.
 * @returns {Promise<Array<number>>} A promise that resolves with an array of strategy IDs.
 */
export const getAvailableStrategies = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.analysis_date) {
    params.append('analysis_date', filters.analysis_date);
  }

  const url = `${API_BASE_URL}/price-range-analysis/strategies?${params.toString()}`;
  
  return fetchWithAuth(url);
};

/**
 * Get detailed information about strategies for price range analysis
 * @param {Object} [filters={}] - Optional filters.
 * @param {string} [filters.analysis_date] - The date for the analysis in 'YYYY-MM-DD' format. If not provided, the backend defaults to the latest.
 * @returns {Promise<Array<Object>>} A promise that resolves with an array of strategy objects containing id and name.
 */
export const getStrategiesDetails = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.analysis_date) {
    params.append('analysis_date', filters.analysis_date);
  }

  const url = `${API_BASE_URL}/price-range-analysis/strategies/details?${params.toString()}`;
  
  return fetchWithAuth(url);
};

/**
 * Get detailed signals for a specific asset and strategy
 * @param {Object} params - Parameters for the signals request
 * @param {number} params.asset_id - The asset ID
 * @param {number} params.strategy_id - The strategy ID
 * @param {string} [params.analysis_date] - The analysis date (optional, defaults to latest)
 * @returns {Promise<Object>} A promise that resolves with signal details
 */
export const getAnalysisSignals = async ({ asset_id, strategy_id, analysis_date }) => {
  const params = new URLSearchParams();
  params.append('asset_id', asset_id);
  params.append('strategy_id', strategy_id);
  if (analysis_date) {
    params.append('analysis_date', analysis_date);
  }

  const url = `${API_BASE_URL}/price-range-analysis/signals?${params.toString()}`;
  
  return fetchWithAuth(url);
};

/**
 * Get available dates for price range analysis
 * @param {number} [limit=30] - Limit the number of dates returned
 * @returns {Promise<Array<string>>} A promise that resolves with available dates
 */
export const getAvailableDates = async (limit = 30) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  
  const url = `${API_BASE_URL}/price-range-analysis/dates?${params.toString()}`;
  
  return fetchWithAuth(url);
};

/**
 * Get exit signals for a strategy
 * @param {string} strategyId - Strategy ID
 * @returns {Promise<Object>} A promise that resolves with exit signals
 */
export const getExitSignals = async (strategyId) => {
  const url = `${API_BASE_URL}/trading/exit-signals/${strategyId}`;
  return fetchWithAuth(url);
};

/**
 * Get exit signal analysis for a specific position
 * @param {string} positionId - Position ID
 * @returns {Promise<Object>} A promise that resolves with exit analysis data
 */
export const getPositionExitAnalysis = async (positionId) => {
  const url = `${API_BASE_URL}/trading/positions/${positionId}/exit-analysis`;
  return fetchWithAuth(url);
};

// ==================== Strategy Performance API ====================

/**
 * Get all strategies performance data
 * @param {Object} [params={}] - Optional parameters for pagination
 * @param {number} [params.limit=100] - Maximum number of strategies to return
 * @param {number} [params.offset=0] - Number of strategies to skip
 * @returns {Promise<Object>} A promise that resolves with strategies performance data
 */
export const getStrategiesPerformance = async (params = {}) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value);
        }
    });

    const url = `${API_BASE_URL}/strategy-performance/?${searchParams.toString()}`;
    return fetchWithAuth(url);
};

/**
 * 获取首页公开策略高光数据
 * @param {Object} [params={}] - 参数
 * @param {number} [params.limit=12] - 后端抓取的策略数量
 * @param {number} [params.top_n=3] - 返回前端的策略数量
 * @returns {Promise<Object>} 公共策略高光响应
 */
export const getPublicStrategyHighlights = async (params = {}) => {
  const { limit = 12, top_n = 3 } = params;
  const searchParams = new URLSearchParams();
  searchParams.append('limit', limit);
  searchParams.append('top_n', top_n);

  const url = `${API_BASE_URL}/public/strategy-highlights?${searchParams.toString()}`;
  return fetchPublic(url);
};

/**
 * Get strategy performance summary
 * @param {number} strategyId - Strategy ID
 * @param {Object} [params={}] - Optional parameters
 * @param {string} [params.date_from] - Filter transactions from this date
 * @param {string} [params.date_to] - Filter transactions to this date
 * @returns {Promise<Object>} A promise that resolves with strategy performance summary
 */
export const getStrategyPerformanceSummary = async (strategyId, params = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });

  const url = `${API_BASE_URL}/strategy-performance/${strategyId}/summary?${searchParams.toString()}`;
  return fetchWithAuth(url);
};

/**
 * Get strategy performance detail with transactions
 * @param {number} strategyId - Strategy ID
 * @param {Object} [params={}] - Optional parameters
 * @param {string} [params.date_from] - Filter transactions from this date
 * @param {string} [params.date_to] - Filter transactions to this date
 * @param {number} [params.limit=1000] - Maximum number of transactions to return
 * @param {number} [params.offset=0] - Number of transactions to skip
 * @returns {Promise<Object>} A promise that resolves with strategy performance detail
 */
export const getStrategyPerformanceDetail = async (strategyId, params = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });

  const url = `${API_BASE_URL}/strategy-performance/${strategyId}/detail?${searchParams.toString()}`;
  return fetchWithAuth(url);
};

/**
 * Get Monte Carlo limited position metrics for a strategy
 * @param {number} strategyId
 * @returns {Promise<Object>}
 */
export const getStrategyLimitedPositionMetrics = async (strategyId) => {
  const url = `${API_BASE_URL}/strategy-performance/${strategyId}/limited-positions`;
  return fetchWithAuth(url);
};

/**
 * Search strategy transactions by asset name or code
 * @param {number} strategyId - Strategy ID
 * @param {Object} [params={}] - Optional parameters
 * @param {string} params.search - Search keyword
 * @param {string} [params.date_from] - Start date filter
 * @param {string} [params.date_to] - End date filter
 * @param {number} [params.limit=500] - Page size
 * @param {number} [params.offset=0] - Offset for pagination
 * @returns {Promise<Object>} A promise resolving with filtered transactions
 */
export const searchStrategyTransactions = async (strategyId, params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });

  const url = `${API_BASE_URL}/strategy-performance/${strategyId}/transactions?${searchParams.toString()}`;
  return fetchWithAuth(url);
};

/**
 * Get active strategies performance
 * @param {Object} [params={}] - Optional parameters
 * @param {string} [params.date_from] - Filter transactions from this date
 * @param {string} [params.date_to] - Filter transactions to this date
 * @param {number} [params.limit=50] - Maximum number of strategies to return
 * @returns {Promise<Array>} A promise that resolves with active strategies performance
 */
export const getActiveStrategiesPerformance = async (params = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });

  const url = `${API_BASE_URL}/strategy-performance/active/summary?${searchParams.toString()}`;
  return fetchWithAuth(url);
};
